<template>
  <div class="embed-elevation-profile" :class="{ minimized: isMinimized }">
    <ElevationProfile
      :data="chartData"
      :highlights="projectedHighlights"
      :stats="stats"
      :get-map="mapGetter"
      :max-points="150"
      collapsible
      :expandable="false"
      :collapsed="isMinimized"
      @update:collapsed="onCollapsedChange"
      @highlight-click="onHighlightClick"
    />
  </div>
</template>

<script lang="ts">
import { defineComponent, type PropType } from 'vue';
import { MeasurementPrefUtility } from '../../../utils/helpers/MeasurementPref';
import ElevationProfile from '../../components/ElevationProfile.vue';
import {
  buildElevationChartData,
  type ElevationChartData,
} from '../../../utils/elevation/buildElevationChartData';
import {
  projectHighlightsOntoLine,
  type ProjectedHighlight,
} from '../../../utils/elevation/projectHighlights';
import {
  elevationGainMetersForActiveView,
  durationMinutesForActiveView
} from '../../../utils/elevation/plannedTrailStats';
import {
  resolveDistanceMeters,
  resolveDurationSeconds,
  resolveElevationGainMeters
} from '../../../utils/elevation/embedElevationTotals';

interface ElevationStats {
  distanceMeters: number;
  gainMeters: number;
  lossMeters: number;
  durationSeconds: number;
}

export default defineComponent({
  name: 'EmbedElevationProfile',
  components: { ElevationProfile },
  props: {
    geojson: {
      type: Object as PropType<any>,
      default: null
    },
    activeView: {
      type: Object as PropType<{ type: string; dayNumber?: number; trailId?: string | number }>,
      default: () => ({ type: 'full' })
    },
    plannedTrails: {
      type: Array as PropType<any[]>,
      default: () => []
    },
    hikeDays: {
      type: Array as PropType<any[]>,
      default: () => []
    },
    minimized: {
      type: Boolean,
      default: false
    },
    // Hike highlights / waypoints to project onto the profile. Raw hike-highlight
    // shape ({ id/clientId, title, coordinates: [lon, lat], ... }). Defaults to []
    // so the parent (HikeMapEmbed.vue) opts in via `:highlights="hikeHighlights"`.
    highlights: {
      type: Array as PropType<any[]>,
      default: () => []
    },
    // Server-computed totals for callers that already know them (the recording
    // embed). Hikes leave these null and keep deriving from plannedTrails /
    // geometry exactly as before.
    distanceMetersOverride: {
      type: Number as PropType<number | null>,
      default: null
    },
    elevationGainMetersOverride: {
      type: Number as PropType<number | null>,
      default: null
    },
    durationSecondsOverride: {
      type: Number as PropType<number | null>,
      default: null
    }
  },
  emits: ['update:minimized'],
  data() {
    return {
      isMinimized: this.minimized
    };
  },
  computed: {
    filteredFeatures(): any[] {
      if (!this.geojson?.features) return [];

      let features = this.geojson.features;

      if (this.activeView.type === 'day') {
        features = features.filter((f: any) =>
          f.properties?.hikeDayNumber === this.activeView.dayNumber &&
          f.properties?.routeStyle === 'route'
        );
      } else if (this.activeView.type === 'route') {
        features = features.filter((f: any) =>
          f.properties?.ptv2PlannedTrailId != null &&
          String(f.properties.ptv2PlannedTrailId) === String(this.activeView.trailId)
        );
      } else {
        features = features.filter((f: any) => f.properties?.routeStyle === 'route');
      }

      return features;
    },

    multiLineStrings(): any[] {
      return this.filteredFeatures.filter(f => f.geometry?.type === 'MultiLineString');
    },

    allCoordinates(): number[][] {
      const coords: number[][] = [];
      this.multiLineStrings.forEach(feature => {
        if (feature.geometry?.coordinates) {
          feature.geometry.coordinates.forEach((lineString: number[][]) => {
            coords.push(...lineString);
          });
        }
      });
      return coords;
    },

    chartData(): ElevationChartData | null {
      const coords = this.allCoordinates;
      if (!coords.length) return null;
      return buildElevationChartData({
        coordinates: coords,
        maxPoints: 150,
        measurementPref: MeasurementPrefUtility.preference()
      });
    },

    // Precedence for each total: a caller's server-computed override (the
    // recording embed), then the planned trails' stored figure — the same one
    // the hike page, routes panel and apps show — then the geometry, via the
    // shared 6m-threshold builder.
    stats(): ElevationStats {
      const d = this.chartData;
      return {
        distanceMeters: resolveDistanceMeters(this.distanceMetersOverride, d?.maxDistanceMeters ?? 0),
        gainMeters: resolveElevationGainMeters(
          this.elevationGainMetersOverride,
          elevationGainMetersForActiveView(this.plannedTrails, this.activeView),
          d?.elevationGainMeters ?? 0
        ),
        lossMeters: d?.elevationLossMeters ?? 0,
        durationSeconds: this.totalDurationSeconds
      };
    },

    // A route view takes that route's own duration whatever its style; day and
    // full views sum only the main 'route' segments.
    totalDurationSeconds(): number {
      return resolveDurationSeconds(
        this.durationSecondsOverride,
        durationMinutesForActiveView(this.plannedTrails, this.activeView)
      );
    },

    projectedHighlights(): ProjectedHighlight[] {
      const coords = this.allCoordinates;
      const highlights = this.highlights;
      if (coords.length < 2 || !highlights?.length) return [];

      const inputs = highlights
        .map((h) => {
          const c = h?.coordinates || [];
          return {
            id: h?.id ?? h?.clientId,
            name: h?.title || '',
            lon: c[0],
            lat: c[1],
            icon: undefined as string | undefined
          };
        })
        .filter((i) => typeof i.lon === 'number' && typeof i.lat === 'number' && i.id != null);

      return projectHighlightsOntoLine(coords, inputs, {
        maxDistanceMeters: this.chartData?.maxDistanceMeters
      });
    }
  },
  watch: {
    minimized(newVal) {
      this.isMinimized = newVal;
    }
  },
  methods: {
    mapGetter(): any {
      return (window as any).map ?? null;
    },

    // Map the shared component's collapse to the embed's minimize contract so
    // the parent's `:minimized` / `@update:minimized` keeps working.
    onCollapsedChange(value: boolean) {
      this.isMinimized = value;
      this.$emit('update:minimized', value);
    },

    onHighlightClick(highlight: ProjectedHighlight) {
      const map = (window as any).map;
      if (!map || typeof map.flyTo !== 'function') return;
      try {
        map.flyTo({
          center: highlight.snappedLngLat,
          zoom: Math.max(typeof map.getZoom === 'function' ? map.getZoom() : 13, 13)
        });
      } catch (error) {
        console.error('Error panning to highlight:', error);
      }
    }
  }
});
</script>

<style scoped>
.embed-elevation-profile {
  position: absolute;
  bottom: 8px;
  left: 8px;
  right: 8px;
  height: 120px;
  background: rgba(40, 42, 50, 0.92);
  backdrop-filter: blur(8px);
  box-shadow: 0 -2px 12px rgba(0, 0, 0, 0.35);
  border-radius: 8px;
  z-index: 100;
  transition: height 0.2s ease;
  color: #fff;
  overflow: hidden;
}

.embed-elevation-profile.minimized {
  height: 36px;
}

/* Outer wrapper owns positioning + chrome; the shared ElevationProfile fills it. */
.embed-elevation-profile :deep(.elevation-profile) {
  height: 100%;
  background: transparent;
  box-shadow: none;
  border-radius: 8px;
}

/* Responsive adjustments */
@media (max-width: 480px) {
  .embed-elevation-profile {
    max-width: none;
    height: 100px;
  }

  .embed-elevation-profile.minimized {
    height: 32px;
  }
}
</style>
